import React, { useEffect, useState } from 'react';
import { getParagraph } from '../components/componentHelpers';
import { ResourceHub } from './ga/ResourceHub';
import { useLayoutContext } from '../providers/LayoutProvider';
import { HeroImage } from '../components/HeroImage/HeroImage';
import { LeaderCards } from '../components/LeaderCards/LeaderCards';
import { SEO } from '../layout/SEO';
import { processMetatags } from '../shared/metaTags';
import { graphql, Link } from 'gatsby';
import { cewPageSchema } from '../seo/cewPageSchema';
import { GenericHeaderAndContent } from '../components/GenericHeaderAndContent/GenericHeaderAndContent';
import { CURRENT_SITE } from '../../config-sites';
import { AnalyticsController } from '../layout/Analytics';
import { Schema } from '../components/Schema/Schema';
import { helixAnalytics } from '../shared/analytics/HelixAnalytics';
import LeaderPage from '../components/LeaderPage/LeaderPage';
import './GenericPage.scss';

export default function LandingPage({ data, pageContext }) {
    const [maxContentWidth, setMaxContentWidth] = useState<number>();
    const { isPolicy } = useLayoutContext();
    const { genericPage: page } = data;
    // const { isDraft, revisionInfo, isPreview } = pageContext;

    const renderParagraphs = (page: any) => {
        if (page.fields.url === `/resource-hub`) {
            return;
        } else if (
            page.fields.url === `/leadership` ||
            page.fields.url === `/executive-leadership`
        ) {
            return (
                page.components
                    // Removes text blocks as these are queried for and added by the Leader Grid
                    ?.filter(
                        (paragraph) =>
                            paragraph.type === `paragraph__investor_card`
                    )
                    ?.map((paragraph) => getParagraph(paragraph, isPolicy))
            );
        } else {
            return page.components?.map((paragraph) =>
                getParagraph(paragraph, isPolicy)
            );
        }
    };

    useEffect(() => {
        AnalyticsController.pageView({
            pageType: `Landing Page`,
        });

        helixAnalytics.trackPageView(data.genericPage?.title, `Generic Page`);
    }, []);

    // if (!isPreview && isDraft && !revisionInfo.affected) {
    //     return;
    // }

    if (page.fields.url === `/leadership`) {
        return (
            <div className="leadership-page">
                {page.top_nav && (
                    <nav className="top-nav">
                        <div className="kite-container">
                            <Link to={page.top_nav.uri}>
                                {page.top_nav.title}
                            </Link>
                        </div>
                    </nav>
                )}
                <div className="kite-container">
                    <HeroImage
                        inputs={{
                            hideHero: page.field_hide_hero,
                            heroTitle: page.title,
                            heroText: page.subtitle,
                            eyebrowText: page.eyebrow?.name,
                            imageAlt: page.hero_image?.field_media_image_1?.alt,
                            image: page.hero_image?.relationships
                                ?.field_media_image_1?.localFile,
                            homepage: page.fields?.url === `/homepage`,
                            leadershipPage: true,
                        }}
                    />
                </div>
                {page.text_title && (
                    <>
                        <GenericHeaderAndContent
                            inputs={{
                                title: page.title,
                                body: page.subtitle,
                                largeText: true,
                                h1: true,
                                skinny: true,
                            }}
                        />
                    </>
                )}
                <LeaderPage
                    edic={false}
                    maxGridWidth={maxContentWidth}
                    setMaxGridWidth={setMaxContentWidth}
                />
                <div className="leadership-page__cta">
                    <div style={{ maxWidth: `${maxContentWidth}px` }}>
                        {renderParagraphs(page)}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            {page.top_nav && (
                <nav className="top-nav">
                    <div className="kite-container">
                        <Link to={page.top_nav.uri}>{page.top_nav.title}</Link>
                    </div>
                </nav>
            )}
            {!isPolicy && (
                <HeroImage
                    inputs={{
                        hideHero: page.field_hide_hero,
                        heroTitle: page.title,
                        heroText: page.subtitle,
                        eyebrowText: page.eyebrow?.name,
                        imageAlt: page.hero_image?.field_media_image_1?.alt,
                        image: page.hero_image?.relationships
                            ?.field_media_image_1?.localFile,
                        homepage: page.fields?.url === `/homepage`,
                    }}
                />
            )}
            {page.text_title && (
                <>
                    <GenericHeaderAndContent
                        inputs={{
                            title: page.title,
                            body: page.subtitle,
                            largeText: true,
                            h1: true,
                            skinny: true,
                        }}
                    />
                </>
            )}
            {page.fields.url === `/edic` && <LeaderCards edic={true} />}
            {renderParagraphs(page)}
            {page.fields.url === `/resource-hub` && (
                <ResourceHub paragraphs={page.components} />
            )}
        </>
    );
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const Head = ({ data }) => {
    const { genericPage: page } = data;
    const { isPolicy } = useLayoutContext();
    const schemaComponent =
        page.relationships?.field_schema?.relationships?.field_key_values?.find(
            (schema) => {
                return schema.type === `paragraph__custom_schema`;
            }
        ) ?? page.relationships?.field_schema?.relationships?.field_key_values;
    return (
        <SEO
            location={page.fields.url
                .replace(`/homepage`, `/`)
                .replace(`/home`, `/`)}
            isPolicy={isPolicy}
        >
            {processMetatags(page.metatag, isPolicy)}
            {CURRENT_SITE.id !== `ga` &&
                [`/`, `/home`, `/homepage`, `/about-charter`].includes(
                    page.fields.url
                ) && (
                    <script
                        type="application/ld+json"
                        dangerouslySetInnerHTML={{
                            __html: cewPageSchema,
                        }}
                    ></script>
                )}
            {schemaComponent && <Schema node={schemaComponent} />}
        </SEO>
    );
};

export const query = graphql`
    query ($id: String!) {
        genericPage(id: { eq: $id }) {
            fields {
                url
            }
            title
            body
            text_title
            top_nav {
                title
                uri
            }
            metatag {
                tag
                attributes {
                    content
                    href
                    name
                    rel
                    property
                }
            }
            field_hide_hero
            hero_image {
                field_media_image_1 {
                    alt
                }
                relationships {
                    field_media_image_1 {
                        localFile {
                            childImageSharp {
                                ...ImageFullWidthSettings
                            }
                        }
                    }
                }
            }
            eyebrow {
                name
            }
            subtitle
            relationships {
                field_schema {
                    relationships {
                        field_key_values {
                            type: __typename
                            ... on paragraph__custom_schema {
                                field_custom_json_schema
                            }
                            ... on paragraph__schema_entry {
                                field_value
                                field_key
                            }
                            ... on paragraph__schema_entry_faq {
                                field_value
                                field_title
                            }
                        }
                    }
                }
            }
            components {
                type: __typename
                ...paragraph__accordion_text_component
                ...paragraph__article_card_trio
                ...paragraph__automatic_listing
                ...paragraph__content_block_2_background
                ...paragraph__callout
                ...paragraph__call_to_action_component
                ...paragraph__card_trio
                ...paragraph__carousel
                ...paragraph__ceo_letter
                ...paragraph__collapsible_table
                ...paragraph__disclaimer
                ...paragraph__featuredblock3article
                ...paragraph__featuredmapcontent
                ...paragraph__field_company_banner
                ...paragraph__hero_block_with_background
                ...paragraph__horizontal_line
                ...paragraph__image_list_component
                ...paragraph__investor_card
                ...paragraph___landing_page_title_and_overview
                ...paragraph__latest_news_and_updates_policy
                ...paragraph__learn_more
                ...paragraph__main_image_carousel
                ...paragraph__media_report
                ...paragraph__news_content_row_of_2
                ...paragraph__overviewblock1
                ...paragraph__overviewblock1image
                ...paragraph__overviewblock3
                ...paragraph__overviewblock4
                ...paragraph__overviewblock5
                ...paragraph__page_title
                ...paragraph__partners
                ...paragraph__pull_quote
                ...paragraph__remote_video_reference
                ...paragraph__resource_hub_cards
                ...paragraph__resource_hub_cards_group_title
                ...paragraph__resourcehubblock2
                ...paragraph__resourcehubhalfvideo
                ...paragraph__resourcehubrelatedcontent
                ...paragraph__resourceshubblock1
                ...paragraph__rich_text
                ...paragraph__single_image
                ...paragraph__text
                ...paragraph__tile_row_1_column
                ...paragraph__tile_row_2_columns
                ...paragraph__tile_row_3_columns
                ...paragraph__tile_row_custom
                ...paragraph__timeline
                ...paragraph__title_and_text
                ...paragraph__video
                ...paragraph__leadership_parent_articles
                ...paragraph__youtube_card
            }
        }
    }
`;
